import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { TooltipDirective } from '@catch/directives';
import { ToastsService } from '@catch/services';
import { TranslocoPipe } from '@jsverse/transloco';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faBookmark as faBookmarkSolid } from '@fortawesome/pro-solid-svg-icons';
import { faBookmark } from '@fortawesome/pro-light-svg-icons';
import { PlatformAuthService } from '../../services/platform-auth.service';
import { PlatformJobboardLoginModalService } from '../../services/platform-jobboard-login-modal.service';
import {
  PlatformJobboardBookmarkPayload,
  PlatformJobsService,
} from '../../services/platform-jobs.service';

@Component({
  selector: 'app-platform-job-bookmark-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FaIconComponent, TooltipDirective, TranslocoPipe],
  template: `
    <fa-icon
      class="table-action"
      [class.active]="saved()"
      [icon]="saved() ? icons.saved : icons.unsaved"
      size="lg"
      [tooltip]="
        (saved()
          ? 'jobboard.platform.jobs.saved.unsave-aria'
          : 'jobboard.platform.jobs.saved.save-aria'
        ) | transloco
      "
      [attr.aria-label]="
        (saved()
          ? 'jobboard.platform.jobs.saved.unsave-aria'
          : 'jobboard.platform.jobs.saved.save-aria'
        ) | transloco
      "
      (click)="onToggle($event)"
    ></fa-icon>
  `,
  styles: `
    :host {
      display: inline-flex;
      align-items: center;
      vertical-align: middle;
      line-height: normal;
    }

    fa-icon {
      color: var(--accent-2);
    }
  `,
})
export class PlatformJobBookmarkButtonComponent {
  private readonly auth = inject(PlatformAuthService);
  private readonly jobs = inject(PlatformJobsService);
  private readonly loginModal = inject(PlatformJobboardLoginModalService);
  private readonly router = inject(Router);
  private readonly toastsService = inject(ToastsService);

  readonly payload = input.required<PlatformJobboardBookmarkPayload>();

  readonly icons = {
    unsaved: faBookmark,
    saved: faBookmarkSolid,
  };

  readonly saved = computed(() => {
    const payload = this.payload();
    this.jobs.savedJobs();
    return this.jobs.isJobSaved(payload.jobId, payload.companyId);
  });

  /**
   * Toggles the bookmark or opens login when the visitor is anonymous.
   * @param event - Click event from the listing card
   */
  onToggle(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.auth.loggedIn()) {
      const payload = this.payload();
      this.loginModal.open({
        redirectUrl: this.router.url,
        afterLogin: () => this.applyBookmark(payload),
      });
      return;
    }
    this.applyBookmark(this.payload());
  }

  /**
   * Saves or removes a job bookmark and shows a toast.
   * @param payload - Bookmark payload for the job
   */
  private applyBookmark(payload: PlatformJobboardBookmarkPayload): void {
    const saved = this.jobs.toggleSavedJob(payload);
    this.toastsService.info(
      saved
        ? 'jobboard.platform.jobs.saved.saved-toast'
        : 'jobboard.platform.jobs.saved.removed-toast'
    );
  }
}
